/**
 * Pure diff for the Add-to-List dialog's multi-select mode: given the current
 * membership of a bulk selection across a set of lists and the user's desired
 * checkbox state, compute which lists to add records to and which to remove
 * them from. Shared by the preview (by list name) and production (by list id)
 * paths — the `ids` are whatever key each mode uses.
 */

export type MembershipState = "all" | "some" | "none";
export type CheckState = "checked" | "unchecked" | "indeterminate";

/**
 * @param ids     list keys to consider (list ids in production, names in preview)
 * @param initial current membership per id ("all"/"some"/"none")
 * @param checked desired checkbox state per id
 *
 * Rules:
 * - checked   + not already "all"  → add (records not yet in it get added)
 * - unchecked + not already "none" → remove
 * - indeterminate → no change (partial membership preserved)
 */
export function computeMembershipDiff(
  ids: string[],
  initial: Record<string, MembershipState | undefined>,
  checked: Record<string, CheckState | undefined>,
): { toAdd: string[]; toRemove: string[] } {
  const toAdd: string[] = [];
  const toRemove: string[] = [];
  for (const id of ids) {
    const was = initial[id];
    const now = checked[id] ?? "unchecked";
    if (now === "checked" && was !== "all") toAdd.push(id);
    else if (now === "unchecked" && was !== "none") toRemove.push(id);
  }
  return { toAdd, toRemove };
}
